import React, { useEffect, useState } from "react";
import type { TableProps } from "antd";
import { Popconfirm, Space, Table } from "antd";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import moment from "moment";
import { getCommonState, getUserState } from "../../redux/store/getState";
import studentId from "../../utils/studentId";
import {
  deleteUser,
  filterUser,
  getUser,
} from "../../redux/apiActions/userActions";
import { User } from "../../redux/apiSlice/userSlice";
import { IconPrimary } from "../../components";
import { DeleteIcon } from "../../assets";
import UserForm from "../../forms/UserForm";
import useSearch from "../../hooks/useSearch";
import { getAtc } from "../../redux/apiActions/atcActions";
import { clearFilterValues } from "../../redux/apiSlice/commonSlice";
import getUserType from "../../utils/getUserType";
import useUser from "../../hooks/useUser";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const UserTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { users, loading } = useAppSelector(getUserState);
  const { filterFrom, filterTo } = useAppSelector(getCommonState);
  const [data, setData] = useState<User[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  useSearch(users, setData);

  const userDeletePermission = useUser();

  useEffect(() => {
    dispatch(getAtc());
  }, [dispatch]);

  useEffect(() => {
    if (filterFrom && filterTo) {
      dispatch(filterUser({ from: filterFrom, to: filterTo }));
    } else {
      dispatch(getUser());
    }
  }, [filterFrom, filterTo, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearFilterValues());
    };
  }, [dispatch]);

  const columns: ColumnsType<User> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },
    {
      title: "Student ID",
      dataIndex: "id",
      render: (_, record) => <>{studentId(record)}</>,
    },

    {
      title: "Username",
      dataIndex: "username",
    },

    {
      title: "Email ID",
      dataIndex: "email",
    },

    {
      title: "User Type",
      dataIndex: "userType",
      render: (_, record) => <>{getUserType(record.permissions)}</>,
    },

    {
      title: "ATC Name",
      dataIndex: "atc",
      render: (atc) => <>{atc.name}</>,
    },

    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <UserForm record={record} />
          <Popconfirm
            title="Delete the user"
            description="Are you sure to delete the user?"
            onConfirm={() => dispatch(deleteUser(record.id))}
            okText="Yes"
            cancelText="No"
          >
            <IconPrimary disabled={!userDeletePermission}>
              <DeleteIcon />
            </IconPrimary>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  return (
    <Table
      style={{ marginTop: 20, whiteSpace: "pre" }}
      loading={loading.get}
      columns={columns}
      rowKey={(record) => record.id}
      dataSource={data}
      onChange={(p) =>
        setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
      }
      pagination={pagination}
      rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
    />
  );
};

export default UserTable;
